/**
 * ExecutionEventModal.tsx
 * T14: Modal for logging execution events on a trade.
 *
 * Handles two primary flows:
 *   - Draft mode (tradeExecution === null): first execution action triggers
 *     TradePromotionService.promote(), converting the draft to a persistent
 *     Trade + TradeExecution + first TradeExecutionEvent.
 *   - Persistent-trade mode (tradeExecution !== null): appends additional events
 *     via TradeExecutionService (logOrder, logFill, logClose).
 *
 * T14 Fix #2: operationId is generated once per modal open — prevents duplicate
 * events and balance rows on double-submit. On error, a new operationId is
 * generated so the user can retry safely.
 *
 * T14 Fix #4: exit quantity ≤ remainingQuantity is validated in the modal form
 * before calling logClose(). The service enforces it as a hard guard too.
 *
 * T16: order types narrowed to limit/stop only (market removed — it is a fill,
 * not a resting order; stop-limit removed — requires two prices, deferred).
 * P&L Amount removed — it is a calculated field, not a user input (S03 scope).
 * SL/TP removed from fill form — belong on OCO bracket orders (WU2D scope).
 * Contextual fill from order row: direction locked, qty capped at order qty,
 * price pre-seeded from order price.
 *
 * @see TradePromotionService.promote — draft → Trade promotion (draft mode)
 * @see TradeExecutionService.logOrder — non-financial order event (persistent)
 * @see TradeExecutionService.logFill  — entry-fill event (persistent)
 * @see TradeExecutionService.logClose — partial/final-exit event (persistent)
 */

// 1. External dependencies
import React, { useState, useEffect, useRef, useCallback } from 'react';

// 2. Internal absolute imports
import {
  Modal, ModalContent, ModalHeader, ModalBody, ModalFooter,
  Button, Input, Select, SelectItem,
  useToastNotification,
} from '@enfomentis/ui';
import { tradePromotionService } from '@/services/TradePromotionService';
import { tradeExecutionService } from '@/services/TradeExecutionService';
import type { FillCancelAllocation } from '@/services/TradeExecutionService';
import { generateUUID } from '@/utils';
import type { TradeExecution } from '@/types/schema/TradeExecution';
import type { TradeSetupDraft } from '@/types/transient/TradeDraft';
import type { InstrumentSnapshot } from '@/types/shared/InstrumentSnapshot';

// ── Types ─────────────────────────────────────────────────────────────────────

export interface ExecutionEventModalProps {
  isOpen: boolean;
  onClose: () => void;
  /**
   * 'order' = order-placed event (limit or stop only)
   * 'fill'  = entry-fill / partial-exit / final-exit
   * 'amend' = order-amended (requires targetOrderEventId)
   * 'cancel' = order-cancelled (requires targetOrderEventId)
   */
  mode: 'order' | 'fill' | 'amend' | 'cancel';
  /**
   * Current TradeExecution projection — null means draft mode (no promotion yet).
   * Caller must not open this modal until tradeExecution has finished loading in
   * persistent mode.
   */
  tradeExecution: TradeExecution | null;
  /** Session draft ID — stamped on TradeExecutionEvent.sessionId */
  sessionId?: string;
  /**
   * Draft to promote — required when tradeExecution is null (draft mode).
   * Caller (TradeDrawer) builds this from current form state and injects accountId.
   */
  draftForPromotion?: TradeSetupDraft;
  /**
   * Instrument snapshot — required when tradeExecution is null (draft mode).
   * Captured asynchronously by TradeDrawer on InstrumentDropdown change.
   * Also used to derive the price step for decimal input.
   */
  instrumentSnapshot?: InstrumentSnapshot;
  /**
   * ID of the original `order-placed` event being targeted.
   * Required for 'amend' and 'cancel' modes.
   * Also used for 'fill' mode when opened via "Order Filled" on an order row —
   * links the fill back to its originating order for partial-fill tracking.
   */
  targetOrderEventId?: string;
  /**
   * Pre-seeded direction for fill mode — set when opened via "Order Filled" on an order row.
   * Overrides the default 'long' seed in the reset effect.
   */
  initialDirection?: 'long' | 'short';
  /**
   * Pre-seeded quantity for fill mode — set when opened via "Order Filled" on an order row.
   * Pre-fills at the remaining order quantity.
   */
  initialQuantity?: number;
  /**
   * Pre-seeded price for fill mode — set when opened via "Order Filled" on an order row.
   * Pre-fills at the order price; user may adjust for slippage.
   */
  initialPrice?: number;
  /**
   * Maximum fill quantity allowed — set when opened via "Order Filled" on an order row.
   * Equals the remaining unfilled quantity for that order. User cannot exceed this value.
   */
  maxQuantity?: number;
  /**
   * Override the fill sub-mode on open.
   * 'entry' = Add to Position (logFill); 'exit' = Exit Position (logClose).
   * When omitted, the modal defaults to 'exit' for open/partial-exit positions.
   * Pass 'entry' when the caller is the "Add to Position" button so the modal
   * opens on the correct sub-mode without requiring the user to toggle.
   */
  initialFillSubMode?: 'entry' | 'exit';
  /**
   * When true, direction is displayed as a read-only badge in fill mode and the
   * fill sub-mode toggle is hidden. Set when a pre-seeded direction exists —
   * either from an order row fill or from "Add to Position" on an established position.
   * Also set for SL/TP order placement where direction is always locked to opposite of position.
   */
  lockDirection?: boolean;
  /**
   * T02 (WU2E): Pre-seed the order type on open (for SL/TP placement — 'stop' or 'limit').
   * When omitted, defaults to 'limit' (existing behaviour).
   */
  initialOrderType?: 'limit' | 'stop';
  /**
   * T02 (WU2E): When true, the order type selector is hidden and shown as a read-only label.
   * Set for SL/TP placement where the type is fixed by the calling button.
   */
  lockOrderType?: boolean;
  /**
   * T02 (WU2E): Prices of the active opposite-type exit orders, for client-side relational
   * price validation at submit time. Provided by TradeDrawer when opening SL/TP modals:
   *   - SL modal → passes active TP prices; TP modal → passes active SL prices.
   * Validation is a UI convenience; the service enforces the constraint as the hard boundary.
   */
  relationalOpponentPrices?: number[];
  /**
   * Current position direction — used in order mode to determine which direction
   * is "opposite" for the order placement reservation cap.
   */
  positionDirection?: 'long' | 'short';
  /**
   * Maximum quantity for a new opposite-direction order. When the user selects
   * the opposite direction in order mode, quantity is capped at this value.
   * Zero means the position is fully reserved by existing opposite-direction orders.
   */
  maxOppositeOrderQty?: number;
  /**
   * Maximum quantity for an opposite-direction order amendment. Pre-computed by
   * TradeDrawer as `maxOppositeOrderQty + thisOrder.remainingQuantity` — gives
   * back the order's own reservation before applying the cap. Undefined for
   * same-direction amends (no cap).
   */
  maxAmendQty?: number;
  /**
   * T03 (WU2E): Active exit orders eligible for cancel allocation on this fill.
   * Provided by TradeDrawer when opening the fill modal in exit mode.
   * Active = opposite direction, not cancelled, not fully filled, orderType stop or limit.
   * Excludes the order being directly filled (if targetOrderEventId is set).
   * When non-empty and fillSubMode === 'exit', the cancel-allocation panel is shown.
   */
  activeExitOrdersForAllocation?: Array<{
    id: string;
    orderType: 'stop' | 'limit';
    currentQuantity: number;
    filledQuantity: number;
    remainingQuantity: number;
    price: number | undefined;
  }>;
  /**
   * Called on successful commit. Caller reloads Trade + TradeExecution from DB
   * using the returned IDs.
   */
  onSuccess: (result: { tradeId: string; tradeExecutionId: string }) => Promise<void>;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

/** Returns a datetime-local string (YYYY-MM-DDTHH:MM) for the current local time. */
const toDatetimeLocal = (): string => {
  const now = new Date();
  const pad = (n: number): string => n.toString().padStart(2, '0');
  return (
    `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}` +
    `T${pad(now.getHours())}:${pad(now.getMinutes())}`
  );
};

/** Strips internal service prefixes from error messages before user display. */
const normaliseServiceError = (message: string): string =>
  message
    .replace(/^\[TradePromotionService\]\s*/, '')
    .replace(/^\[TradeExecutionService\]\s*/, '');

/**
 * Returns true for non-recoverable assertion failures (DB consistency issues,
 * transaction result assertions) that should trigger a toast instead of an
 * actionable inline error.
 */
const isUnexpectedServiceError = (message: string): boolean =>
  (message.includes('[TradeExecutionService]') || message.includes('[TradePromotionService]')) &&
  (message.includes('not found') || message.includes('without a result'));

// ── Component ─────────────────────────────────────────────────────────────────

export const ExecutionEventModal: React.FC<ExecutionEventModalProps> = ({
  isOpen,
  onClose,
  mode,
  tradeExecution,
  sessionId,
  draftForPromotion,
  instrumentSnapshot,
  targetOrderEventId,
  initialDirection,
  initialQuantity,
  initialPrice,
  maxQuantity,
  initialFillSubMode,
  lockDirection,
  initialOrderType,
  lockOrderType,
  relationalOpponentPrices,
  positionDirection,
  maxOppositeOrderQty,
  maxAmendQty,
  activeExitOrdersForAllocation,
  onSuccess,
}) => {
  // ── Toast ────────────────────────────────────────────────────────────────────

  const { showToast } = useToastNotification();

  // ── Form state ───────────────────────────────────────────────────────────────

  const [occurredAt, setOccurredAt] = useState('');
  const [direction, setDirection] = useState<'long' | 'short'>('long');
  const [quantity, setQuantity] = useState('');
  const [price, setPrice] = useState('');
  /**
   * Fill sub-mode for open/partial-exit positions.
   * 'entry' = Add to Position (logFill); 'exit' = Exit Position (logClose).
   * Default to 'exit' when position is already open — most likely intent.
   */
  const [fillSubMode, setFillSubMode] = useState<'entry' | 'exit'>('entry');
  const [orderType, setOrderType] = useState<'limit' | 'stop'>('limit');
  /** T03 (WU2E): maps orderEventId → cancelQtyString for the cancel-allocation panel */
  const [cancelAllocations, setCancelAllocations] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  /**
   * T14 Fix #2: operationId generated once per modal open.
   * Prevents duplicate events / balance rows on double-submit.
   * Regenerated after an error so the user can retry safely.
   */
  const operationIdRef = useRef<string>('');

  // ── Reset / seed on open ─────────────────────────────────────────────────────

  useEffect(() => {
    if (!isOpen) return;

    // Fix #2: fresh operationId per open
    operationIdRef.current = generateUUID();

    setOccurredAt(toDatetimeLocal());
    // Pre-seed direction from prop (for "Order Filled" from order row), else default 'long'
    setDirection(initialDirection ?? 'long');
    // Pre-seed quantity from prop (for "Order Filled" from order row), else empty
    setQuantity(initialQuantity !== undefined ? String(initialQuantity) : '');
    // Pre-seed price from prop (for "Order Filled" from order row), else empty
    setPrice(initialPrice !== undefined ? String(initialPrice) : '');
    setOrderType(initialOrderType ?? 'limit');
    // Default fill sub-mode: honour caller's hint; fall back to 'exit' when position
    // is already open (most likely intent), otherwise 'entry'.
    const status = tradeExecution?.executionStatus;
    setFillSubMode(
      initialFillSubMode ??
      (status === 'open' || status === 'partial-exit' ? 'exit' : 'entry')
    );
    setCancelAllocations({});
    setSubmitError(null);
  }, [isOpen, tradeExecution?.executionStatus, initialDirection, initialQuantity, initialPrice, initialFillSubMode, initialOrderType]);

  // ── Derived state ────────────────────────────────────────────────────────────

  const isDraftMode = tradeExecution === null;
  const executionStatus = tradeExecution?.executionStatus ?? null;

  /** True when the position is open/partially-closed — show Add/Exit toggle */
  const showFillSubModeToggle =
    mode === 'fill' &&
    !isDraftMode &&
    !lockDirection &&
    (executionStatus === 'open' || executionStatus === 'partial-exit');

  /** Show direction field for order-placed events (when not locked) and non-locked entry fills */
  const showDirectionField =
    (mode === 'order' && !lockDirection) ||
    (mode === 'fill' &&
      !lockDirection &&
      (isDraftMode || executionStatus === 'pending' || fillSubMode === 'entry'));

  const remainingQty = tradeExecution?.remainingQuantity;

  /** Effective max qty for opposite-direction orders — drives the reactive label hint. */
  const effectiveOrderMaxQty =
    mode === 'order' && positionDirection !== undefined && direction !== positionDirection
      ? maxOppositeOrderQty
      : undefined;

  /** True for amend/cancel — persistent-only modes that target a specific order row */
  const isOrderManagementMode = mode === 'amend' || mode === 'cancel';

  /** Decimal step for price inputs — from instrument snapshot (draft or persistent mode) */
  const priceIncrementSize =
    instrumentSnapshot?.incrementSize ??
    tradeExecution?.instrumentSnapshot?.incrementSize;
  const priceStep = priceIncrementSize !== undefined ? String(priceIncrementSize) : 'any';

  // ── Submit ───────────────────────────────────────────────────────────────────

  const handleSubmit = useCallback(async (): Promise<void> => {
    if (isSubmitting) return;
    setSubmitError(null);

    // Validate time
    if (!occurredAt) {
      setSubmitError('Time is required.');
      return;
    }

    const qty = quantity ? parseFloat(quantity) : 0;
    const px = price ? parseFloat(price) : 0;

    if (mode === 'order') {
      if (qty <= 0) {
        setSubmitError('Quantity must be greater than 0.');
        return;
      }
      if (px <= 0) {
        setSubmitError('Price is required.');
        return;
      }
      // Opposite-direction order placement cap: quantity cannot exceed the
      // position's available reducible quantity minus existing opposite orders.
      if (
        positionDirection !== undefined &&
        direction !== positionDirection &&
        maxOppositeOrderQty !== undefined
      ) {
        if (maxOppositeOrderQty === 0) {
          setSubmitError('Position is fully reserved by existing opposite-direction orders. Cancel or fill an existing order first.');
          return;
        }
        if (qty > maxOppositeOrderQty) {
          setSubmitError(`Opposite-direction order quantity cannot exceed ${maxOppositeOrderQty} (remaining unreserved position quantity).`);
          return;
        }
      }
      // T02 (WU2E): relational price validation for locked SL/TP order placement.
      // UI-level convenience check — the service enforces this as the hard boundary.
      if (
        lockOrderType &&
        relationalOpponentPrices !== undefined &&
        relationalOpponentPrices.length > 0 &&
        positionDirection !== undefined
      ) {
        const isLong = positionDirection === 'long';
        const isTP = orderType === 'limit';
        const mustBeAbove = isLong === isTP;
        const violated = relationalOpponentPrices.some(oppPrice =>
          mustBeAbove ? px <= oppPrice : px >= oppPrice
        );
        if (violated) {
          const orderLabel = isTP ? 'Take Profit' : 'Stop Loss';
          const opponentLabel = isTP ? 'Stop Loss' : 'Take Profit';
          const direction_ = isLong ? 'long' : 'short';
          setSubmitError(
            `${orderLabel} price must be ${mustBeAbove ? 'above' : 'below'} all active ${opponentLabel} prices (${direction_} position).`
          );
          return;
        }
      }
    }

    if (mode === 'fill') {
      if (qty <= 0) {
        setSubmitError('Quantity must be greater than 0.');
        return;
      }
      if (px <= 0) {
        setSubmitError('Price must be greater than 0.');
        return;
      }
      // Contextual fill: qty cannot exceed remaining order quantity
      if (maxQuantity !== undefined && qty > maxQuantity) {
        setSubmitError(`Fill quantity cannot exceed order quantity (${maxQuantity}).`);
        return;
      }
      // Fix #4 (UI-level gate — also enforced at service level in logClose)
      if (
        fillSubMode === 'exit' &&
        remainingQty !== undefined &&
        qty > remainingQty
      ) {
        setSubmitError(`Exit quantity cannot exceed remaining quantity (${remainingQty}).`);
        return;
      }
    }

    if (mode === 'amend') {
      if (qty <= 0 && px <= 0) {
        setSubmitError('Provide a new quantity, new price, or both.');
        return;
      }
      // Opposite-direction order amend cap: new quantity cannot exceed the
      // position's available reducible quantity plus this order's own reservation.
      if (qty > 0 && maxAmendQty !== undefined && qty > maxAmendQty) {
        setSubmitError(`Amended quantity cannot exceed ${maxAmendQty} (remaining unreserved position quantity plus this order's current reservation).`);
        return;
      }
    }

    const operationId = operationIdRef.current;
    const sessionId_ = sessionId ?? '';
    // Parse datetime-local as local time
    const occurredAtISO = new Date(occurredAt).toISOString();

    setIsSubmitting(true);
    try {
      if (isDraftMode) {
        // ── Draft mode: promote on first execution action ──────────────────────
        if (!draftForPromotion) {
          setSubmitError('Draft not available. Please close and reopen the drawer.');
          return;
        }
        if (!instrumentSnapshot) {
          setSubmitError('Select an instrument before logging.');
          return;
        }
        if (!draftForPromotion.accountId) {
          setSubmitError('Select a trading account before logging.');
          return;
        }

        const result = await tradePromotionService.promote({
          draft: draftForPromotion,
          sessionId: sessionId_,
          operationId,
          eventType: mode === 'order' ? 'order-placed' : 'entry-fill',
          accountId: draftForPromotion.accountId,
          instrumentSnapshot,
          occurredAt: occurredAtISO,
          ...(mode === 'order' ? {
            direction,
            quantity: qty,
            price: px,
            orderType,
          } : mode === 'fill' ? {
            direction,
            quantity: qty,
            price: px,
            // No balanceRow — P&L deferred to S03 reconciliation
          } : {}),
        });

        await onSuccess({ tradeId: result.tradeId, tradeExecutionId: result.tradeExecutionId });
        onClose();
      } else {
        // ── Persistent mode: append event to existing TradeExecution ───────────
        const { id: tradeExecutionId, tradeId } = tradeExecution;

        if (mode === 'amend') {
          if (!targetOrderEventId) {
            setSubmitError('Target order not set. Close and reopen the drawer.');
            return;
          }
          await tradeExecutionService.logAmend({
            tradeExecutionId,
            tradeId,
            sessionId: sessionId_,
            operationId,
            occurredAt: occurredAtISO,
            targetOrderEventId,
            ...(qty > 0 ? { quantity: qty } : {}),
            ...(px > 0 ? { price: px } : {}),
          });
        } else if (mode === 'cancel') {
          if (!targetOrderEventId) {
            setSubmitError('Target order not set. Close and reopen the drawer.');
            return;
          }
          await tradeExecutionService.logCancelOrder({
            tradeExecutionId,
            tradeId,
            sessionId: sessionId_,
            operationId,
            occurredAt: occurredAtISO,
            targetOrderEventId,
          });
        } else if (mode === 'order') {
          await tradeExecutionService.logOrder({
            tradeExecutionId,
            tradeId,
            sessionId: sessionId_,
            operationId,
            occurredAt: occurredAtISO,
            direction,
            quantity: qty,
            price: px,
            orderType,
          });
        } else if (fillSubMode === 'entry' || executionStatus === 'pending') {
          // Entry fill — new position or adding to existing
          await tradeExecutionService.logFill({
            tradeExecutionId,
            tradeId,
            sessionId: sessionId_,
            operationId,
            direction,
            quantity: qty,
            price: px,
            occurredAt: occurredAtISO,
            ...(targetOrderEventId !== undefined ? { targetOrderEventId } : {}),
            // No balanceRow — P&L deferred to S03 reconciliation
          });
        } else {
          // Exit — partial or final based on quantity vs remainingQty
          const isFinalExit = remainingQty !== undefined && qty >= remainingQty;
          const parsedAllocations: FillCancelAllocation[] = (activeExitOrdersForAllocation ?? [])
            .map(o => ({ orderEventId: o.id, cancelQty: Number(cancelAllocations[o.id] ?? '0') }))
            .filter(a => a.cancelQty > 0);
          await tradeExecutionService.logClose({
            tradeExecutionId,
            tradeId,
            sessionId: sessionId_,
            operationId,
            eventType: isFinalExit ? 'final-exit' : 'partial-exit',
            quantity: qty,
            price: px,
            occurredAt: occurredAtISO,
            ...(targetOrderEventId !== undefined ? { targetOrderEventId } : {}),
            ...(parsedAllocations.length > 0 ? { cancelAllocations: parsedAllocations } : {}),
            // No balanceRow — P&L deferred to S03 reconciliation
          });
        }

        await onSuccess({ tradeId, tradeExecutionId });
        onClose();
      }
    } catch (err) {
      const rawMessage = err instanceof Error ? err.message : 'Failed to save. Please try again.';
      const normalisedMessage = normaliseServiceError(rawMessage);
      if (isUnexpectedServiceError(rawMessage)) {
        console.error('[ExecutionEventModal]', err);
        showToast({ color: 'danger', title: 'Something went wrong', description: 'Please close and try again.' });
        setSubmitError('An unexpected error occurred.');
      } else {
        setSubmitError(normalisedMessage);
      }
      // Fix #2: regenerate operationId so a retry is treated as a fresh attempt
      operationIdRef.current = generateUUID();
    } finally {
      setIsSubmitting(false);
    }
  }, [
    isSubmitting, occurredAt, mode, isDraftMode, fillSubMode, orderType,
    quantity, price, direction, maxQuantity, positionDirection, maxOppositeOrderQty, maxAmendQty,
    lockOrderType, relationalOpponentPrices, cancelAllocations, activeExitOrdersForAllocation,
    remainingQty, executionStatus, draftForPromotion, instrumentSnapshot,
    sessionId, tradeExecution, targetOrderEventId, onSuccess, onClose,
    showToast,
  ]);

  // ── Title ────────────────────────────────────────────────────────────────────

  const modalTitle = (() => {
    if (mode === 'amend') return 'Amend Order';
    if (mode === 'cancel') return 'Cancel Order';
    if (mode === 'order') {
      return isDraftMode ? 'Log Order (Create Trade)' : 'Log Order';
    }
    if (isDraftMode) return 'Log Fill (Create Trade)';
    if (lockDirection && targetOrderEventId) return 'Order Filled';
    return fillSubMode === 'exit' ? 'Exit Position' : 'Add to Position';
  })();

  // ── Render ───────────────────────────────────────────────────────────────────

  return (
    <Modal
      isOpen={isOpen}
      onOpenChange={(open) => { if (!open) onClose(); }}
      size="sm"
    >
      <ModalContent>
        <ModalHeader className="text-sm font-semibold">{modalTitle}</ModalHeader>

        <ModalBody className="flex flex-col gap-3 pb-4">

          {/* Fill sub-mode toggle — open/partial-exit positions, unlinked fills only */}
          {showFillSubModeToggle && (
            <div className="flex gap-2">
              <Button
                size="sm"
                variant={fillSubMode === 'entry' ? 'solid' : 'bordered'}
                color={fillSubMode === 'entry' ? 'primary' : 'default'}
                className="flex-1"
                onPress={() => setFillSubMode('entry')}
              >
                Add to Position
              </Button>
              <Button
                size="sm"
                variant={fillSubMode === 'exit' ? 'solid' : 'bordered'}
                color={fillSubMode === 'exit' ? 'primary' : 'default'}
                className="flex-1"
                onPress={() => setFillSubMode('exit')}
              >
                Exit Position
              </Button>
            </div>
          )}

          {/* Amend fields — new quantity and/or price (at least one required) */}
          {mode === 'amend' && (
            <>
              <p className="text-xs text-text-tertiary">
                Provide the new quantity, new price, or both. Leave blank to keep unchanged.
              </p>
              <div className="flex gap-2">
                <Input
                  label={maxAmendQty !== undefined ? `New Quantity (max ${maxAmendQty})` : 'New Quantity (optional)'}
                  size="sm"
                  variant="bordered"
                  type="number"
                  min="0"
                  step="any"
                  value={quantity}
                  onValueChange={setQuantity}
                  className="flex-1"
                />
                <Input
                  label="New Price (optional)"
                  size="sm"
                  variant="bordered"
                  type="number"
                  min="0"
                  step={priceStep}
                  value={price}
                  onValueChange={setPrice}
                  className="flex-1"
                />
              </div>
            </>
          )}

          {/* Cancel confirmation */}
          {mode === 'cancel' && (
            <p className="text-xs text-text-tertiary">
              Record when this order was cancelled. The order row will be marked as Cancelled.
            </p>
          )}

          {/* Order type — order events only (limit and stop) */}
          {mode === 'order' && !lockOrderType && (
            <Select
              label="Order Type"
              size="sm"
              variant="bordered"
              selectedKeys={[orderType]}
              onSelectionChange={(keys) => {
                if (keys === 'all') return;
                const k = Array.from(keys)[0] as string | undefined;
                if (k === 'limit' || k === 'stop') {
                  setOrderType(k);
                }
              }}
            >
              <SelectItem key="limit">Limit</SelectItem>
              <SelectItem key="stop">Stop</SelectItem>
            </Select>
          )}
          {/* Order type — read-only badge for locked SL/TP placement */}
          {mode === 'order' && lockOrderType && (
            <div className="flex items-center gap-2">
              <span className="text-xs text-text-tertiary">Order Type</span>
              <span className="text-xs font-semibold px-2 py-0.5 rounded bg-default-100 text-text-primary">
                {orderType === 'stop' ? 'Stop' : 'Limit'}
              </span>
            </div>
          )}

          {/* Direction — order events and non-locked entry fills */}
          {showDirectionField && (
            <Select
              label="Direction"
              size="sm"
              variant="bordered"
              selectedKeys={[direction]}
              onSelectionChange={(keys) => {
                if (keys === 'all') return;
                const k = Array.from(keys)[0] as string | undefined;
                if (k === 'long' || k === 'short') setDirection(k);
              }}
            >
              <SelectItem key="long">Long</SelectItem>
              <SelectItem key="short">Short</SelectItem>
            </Select>
          )}

          {/* Direction — read-only badge for locked fills (order fill or Add to Position) */}
          {mode === 'fill' && lockDirection && (
            <div className="flex items-center gap-2">
              <span className="text-xs text-text-tertiary">Direction</span>
              <span className={`text-xs font-semibold px-2 py-0.5 rounded ${
                direction === 'long' ? 'bg-success/10 text-success' : 'bg-danger/10 text-danger'
              }`}>
                {direction === 'long' ? 'Long' : 'Short'}
              </span>
            </div>
          )}

          {/* Direction — read-only badge for locked order placement (SL/TP) */}
          {mode === 'order' && lockDirection && (
            <div className="flex items-center gap-2">
              <span className="text-xs text-text-tertiary">Direction</span>
              <span className={`text-xs font-semibold px-2 py-0.5 rounded ${
                direction === 'long' ? 'bg-success/10 text-success' : 'bg-danger/10 text-danger'
              }`}>
                {direction === 'long' ? 'Long' : 'Short'}
              </span>
            </div>
          )}

          {/* Quantity */}
          {(mode === 'fill' || mode === 'order') && (
            <Input
              label={
                maxQuantity !== undefined
                  ? `Quantity (max ${maxQuantity})`
                  : mode === 'fill' && fillSubMode === 'exit' && remainingQty !== undefined
                    ? `Quantity (max ${remainingQty})`
                    : effectiveOrderMaxQty !== undefined
                      ? `Quantity (max ${effectiveOrderMaxQty})`
                      : 'Quantity'
              }
              size="sm"
              variant="bordered"
              type="number"
              min="0"
              step="any"
              value={quantity}
              onValueChange={setQuantity}
            />
          )}

          {/* Price */}
          {(mode === 'fill' || mode === 'order') && (
            <Input
              label="Price"
              size="sm"
              variant="bordered"
              type="number"
              min="0"
              step={priceStep}
              value={price}
              onValueChange={setPrice}
            />
          )}

          {/* Time */}
          <Input
            label="Time"
            size="sm"
            variant="bordered"
            type="datetime-local"
            value={occurredAt}
            onValueChange={setOccurredAt}
          />

          {/* Draft mode info */}
          {isDraftMode && (
            <p className="text-xs text-text-tertiary">
              This will create a trade record and link it to the current setup.
            </p>
          )}

          {/* T03 (WU2E): Cancel-allocation panel — shown for exit fills when active SL/TP orders exist */}
          {mode === 'fill' && fillSubMode === 'exit' && (activeExitOrdersForAllocation ?? []).length > 0 && (
            <div className="flex flex-col gap-2 rounded-md border border-divider p-2">
              <p className="text-xs font-medium text-text-secondary">Cancel / reduce exit orders</p>
              {(activeExitOrdersForAllocation ?? []).map(order => (
                <div key={order.id} className="flex items-center gap-2">
                  <span className="min-w-0 flex-1 truncate text-xs text-text-tertiary">
                    {order.orderType === 'stop' ? 'SL' : 'TP'}
                    {order.price !== undefined ? ` @ ${order.price}` : ''}
                    {' '}(rem: {order.remainingQuantity})
                  </span>
                  <Input
                    size="sm"
                    variant="bordered"
                    type="number"
                    className="w-20 shrink-0"
                    min={0}
                    max={order.remainingQuantity}
                    value={cancelAllocations[order.id] ?? ''}
                    onValueChange={(v) =>
                      setCancelAllocations(prev => ({ ...prev, [order.id]: v }))
                    }
                    placeholder="0"
                    aria-label={`Cancel quantity for ${order.orderType === 'stop' ? 'Stop Loss' : 'Take Profit'} order`}
                  />
                  <Button
                    size="sm"
                    variant="bordered"
                    onPress={() =>
                      setCancelAllocations(prev => ({
                        ...prev,
                        [order.id]: String(order.remainingQuantity),
                      }))
                    }
                  >
                    All
                  </Button>
                </div>
              ))}
            </div>
          )}

          {/* Error */}
          {submitError !== null && (
            <p className="text-xs text-danger">{submitError}</p>
          )}

        </ModalBody>

        <ModalFooter className="gap-2 pt-0">
          <Button
            size="sm"
            variant="light"
            isDisabled={isSubmitting}
            onPress={onClose}
          >
            Cancel
          </Button>
          <Button
            size="sm"
            color="primary"
            variant="solid"
            isLoading={isSubmitting}
            isDisabled={isSubmitting}
            onPress={() => { void handleSubmit(); }}
          >
            {isOrderManagementMode
              ? (mode === 'cancel' ? 'Confirm Cancel ✓' : 'Amend ✓')
              : isDraftMode ? 'Create Trade ✓' : 'Log ✓'
            }
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
